import experience from '@assets/icons/experience.svg';
import { ErrorMessage, TaAutocomplete, TaInput } from '@components/shared';
import { Editor } from '@components/shared/HtmlEditor/HtmlEditor';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { EditableCard } from '@features/dashboard/specialistDashboard/editableCard/editableCard';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Chip } from '@mui/material';
import Button from '@mui/material/Button';
import { Dictionary, Nullable } from '@utils/util.types';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountries, useCountriesStore } from 'services/countries';
import { User, useUpdateUser, useUsersStore } from 'services/users';
import * as Yup from 'yup';

const WorkValidationSchema = Yup.object().shape({
	years: Yup.number().typeError('fieldNameError'),
});

interface WorkingExperienceForm {
	companyCountry: Nullable<Dictionary>;
	years: string;
	about: string;
}

export const WorkExperienceCard = ({
	readonly = false,
	user: userProp,
}: {
	readonly?: boolean;
	user?: User | null;
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [workExperiences, setWorkExperiences] = useState<{ country: string; year: string }[]>([]);
	const { countries } = useCountriesStore();
	const mutate = useUpdateUser();
	const { user: storeUser } = useUsersStore();

	const [showErrorMessage, setShowErrorMessage] = useState(false);
	const [showCountryErrorMessage, setShowCountryErrorMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const user = userProp || storeUser;

	useCountries();

	const formik = useFormik<WorkingExperienceForm>({
		initialValues: {
			companyCountry: null,
			years: '',
			about: user?.about || '',
		},
		validationSchema: WorkValidationSchema,
		onSubmit: async () => {
			setIsLoading(true);
			if (formik.values.companyCountry || formik.values.years) {
				setShowErrorMessage(true);
				return;
			}
			const value = {
				workingExperiences: workExperiences,
				about: formik.values.about,
			};

			await mutate(value);
			setSnackbarOpen(true);
			setShowErrorMessage(false);
			setShowCountryErrorMessage(false);
			setIsLoading(false);
			setOpen(false);
		},
	});

	useEffect(() => {
		setWorkExperiences(user?.working_experience || []);
	}, [user?.working_experience]);

	const handleAddExperience = () => {
		if (formik.values.companyCountry && formik.values.years) {
			const country = countries.find((country) => country.id === formik.values.companyCountry?.id);

			const isCountryAlreadyAdded = workExperiences.some((exp) => exp.country === country?.name);

			if (isCountryAlreadyAdded) {
				setShowCountryErrorMessage(true);

				return;
			}

			setWorkExperiences([
				...workExperiences,
				{ country: country?.name as string, year: formik.values.years.toString() },
			]);
			setShowErrorMessage(false);
			setShowCountryErrorMessage(false);
			formik.setFieldValue('companyCountry', null);
			formik.setFieldValue('years', '');
		}
	};

	const workingYearsSum = useMemo(
		() =>
			user?.working_experience.reduce((acc: any, curr: any) => acc + parseInt(curr.year), 0) || 0,
		[user?.working_experience]
	);

	const workingCountriesSum = useMemo(
		() => user?.working_experience.map((item: any) => item.country).length || 0,
		[user?.working_experience]
	);

	const handleRemoveExperience = (country: string) => {
		setWorkExperiences(workExperiences.filter((exp) => exp.country !== country));
	};

	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const regex = /^[0-9]$/;
		const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

		if (!regex.test(event.key) && !allowedKeys.includes(event.key)) {
			event.preventDefault();
		}
	};

	return (
		<EditableCard
			onEdit={() => setOpen(true)}
			className="responsive-card"
			title={t('workExperience')}
			readonly={readonly}
		>
			<div className="flex flex-col gap-[20px] pb-5">
				<div className="flex flex-wrap gap-[10px]">
					<img className="text-secondary" src={experience} alt="crop" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					{(user?.working_experience?.length ?? 0) > 0 ? (
						<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">
							{t('workExperienceTitle', {
								years: workingYearsSum,
								country: workingCountriesSum,
							})}
						</p>
					) : (
						<div className="rounded-[8px] bg-disabled p-[1px_10px] text-secondary font-roboto text-[16px] not-italic font-normal leading-[140%]">
							{t('workExperienceEmpty')}
						</div>
					)}
					{user?.working_experience.map((item: any) => (
						<Chip
							key={item.country}
							label={`${item.country} - ${item.year} ${t('years')}`}
							variant="outlined"
							sx={{
								borderRadius: '8px',
								border: '1px solid #CED4DA',
								backgroundColor: '#F8F9FA',
								padding: '1px 10px',
								color: '#7B809A',
								fontFamily: 'Roboto',
								fontSize: '16px',
								fontStyle: 'normal',
								fontWeight: 400,
								lineHeight: '140%',
								height: '24px',
								textTransform: 'camelcase',
								'& .MuiChip-label': {
									padding: 0,
								},
							}}
						/>
					))}
				</div>
				<div className="w-full h-[1px] bg-[#E9ECEF]"></div>
				{user?.about && user.about !== '<p><br></p>' ? (
					<div
						className="text-[#343A40] roboto-regular text-[16px] leading-[140%]"
						dangerouslySetInnerHTML={{ __html: user?.about || 'N/A' }}
					></div>
				) : (
					<div className="rounded-[8px] bg-disabled p-[1px_10px] text-secondary font-roboto text-[16px] not-italic font-normal leading-[140%] w-max">
						{t('noAdditionalInfo')}
					</div>
				)}
			</div>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				onClose={() => {
					formik.resetForm();
					setShowErrorMessage(false);
					setShowCountryErrorMessage(false);
					setOpen(false);
				}}
				title={t('workExperience')}
				showActions
				saveButtonLoading={isLoading}
				disableSaveButton={
					isLoading ||
					(workExperiences.length === 0 &&
						(!formik.values.about || formik.values.about === '<p><br></p>'))
				}
			>
				<div className="flex flex-col gap-[20px] pb-5 mt-[20px]">
					<div
						className={`flex items-end gap-[10px] max-sm:flex-wrap ${formik.touched.years && formik.errors.years ? 'mb-[10px]' : ''}`}
					>
						<div className="flex flex-col gap-[10px] w-[40%] max-sm:w-[100%]">
							<span className="roboto-regular text-[16px] leading-[140%]">{t('workedIn')}</span>
							<div>
								<TaAutocomplete
									options={countries}
									hasFlag
									getOptionLabel={(option) => option.name}
									label={t('country')}
									value={formik.values.companyCountry as unknown as Dictionary}
									onChange={(event, value) => {
										formik.setFieldValue('companyCountry', value || '');
									}}
									error={Boolean(formik.touched.companyCountry && formik.errors.companyCountry)}
								/>
							</div>
						</div>
						<div className="flex items-end gap-[10px] w-[60%] max-sm:w-[100%]">
							<div className="flex flex-col gap-[10px]  w-[50%] max-sm:w-[70%]">
								<span className="roboto-regular text-[16px] leading-[140%]">{t('workedFor')}</span>
								<div>
									<TaInput
										id="years"
										label={t('years')}
										size="small"
										type="number"
										onKeyPress={handleKeyPress}
										value={formik.values.years}
										onChange={formik.handleChange}
										error={formik.touched.years && Boolean(formik.errors.years)}
									/>
									{formik.touched.years && formik.errors.years && (
										<div className="absolute">
											<ErrorMessage
												message={t(formik.errors.years || '', {
													fieldName: t('years'),
												})}
											/>
										</div>
									)}
								</div>
							</div>
							<Button
								type="button"
								variant="outlined"
								className="rounded-[8px] shadow-custom-button !text-dark roboto-bold text-[14px] leading-[140%] gap-[10px] w-[50%] max-sm:w-[30%] !border-none h-max"
								onClick={handleAddExperience}
							>
								<AddIcon className="text-secondary" />
								{t('add')}
							</Button>
						</div>
					</div>
					{showErrorMessage && <ErrorMessage message={t('addExperience')} />}
					{showCountryErrorMessage && <ErrorMessage message={t('countryAlreadyAdded')} />}
					{(workExperiences.length ?? 0) > 0 && (
						<div className="flex flex-wrap gap-[10px]">
							{workExperiences.map((item) => (
								<Chip
									key={item.country}
									label={
										<div className="flex items-center gap-[10px]">
											{`${item.country} - ${item.year} ${t('years')}`}
											<CloseIcon
												onClick={() => handleRemoveExperience(item.country)}
												style={{ cursor: 'pointer' }}
											/>
										</div>
									}
									variant="outlined"
									sx={{
										borderRadius: '8px',
										border: '1px solid #CED4DA',
										backgroundColor: '#F8F9FA',
										padding: '5px 10px',
										color: '#7B809A',
										fontFamily: 'Roboto',
										fontSize: '16px',
										fontStyle: 'normal',
										fontWeight: 400,
										lineHeight: '140%',
										height: '34px',
										textTransform: 'camelcase',
										'& .MuiChip-label': {
											padding: 0,
										},
									}}
								/>
							))}
						</div>
					)}
					<div className="w-full h-[1px] bg-[#E9ECEF]"></div>
					<div className="flex flex-col gap-[10px]">
						<span className="roboto-regular text-[16px] leading-[140%]">{t('aboutMe')}</span>
						<Editor
							value={formik.values.about}
							onBlur={() => formik.setFieldTouched('about', true)}
							placeholder={t('aboutMePlaceholder')}
							onChange={(value) => formik.setFieldValue('about', value)}
						/>
					</div>
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('workExperience'),
				})}
			/>
		</EditableCard>
	);
};
